/**
 * NotaAnclaRepository - Anclas de notas (web Dexie + mobile SQLite).
 *
 * Mismo modelo que PuntoControlRepository: delete() es SOFT-delete (UPDATE
 * is_deleted=true vía sync). Las anclas las crea hoy el chat (aiChatBot) y
 * llegan por pull/realtime; el repo igual soporta escritura local.
 */

import { v4 as uuidv4 } from 'uuid';
import { INotaAnclaRepository, NotaAncla, TipoAncla } from '../interfaces/INotaAnclaRepository';
import { BaseRepository } from './BaseRepository';
import { IDatabaseAdapter, WhereClause } from '../adapters/IDatabaseAdapter';
import { IProfileRepository } from '../interfaces/IProfileRepository';

/** En SQLite el array puede llegar como string JSON si la fila no pasó por el adapter. */
function comoArray(v: unknown): string[] {
  if (Array.isArray(v)) return v as string[];
  if (typeof v === 'string' && v.trim().startsWith('[')) {
    try { return JSON.parse(v); } catch { return []; }
  }
  return [];
}

export class NotaAnclaRepository extends BaseRepository<NotaAncla, string> implements INotaAnclaRepository {
  private readonly NOT_DELETED: WhereClause = { field: 'is_deleted', operator: 'equals', value: false };

  constructor(db: IDatabaseAdapter, profileRepo?: IProfileRepository) {
    super(db, 'nota_anclas', 'nota_anclas');
    this.supportsSoftDelete = true;
    this.autoTimestamp = true;
    if (profileRepo) this.setProfileRepository(profileRepo);
  }

  async add(data: Omit<NotaAncla, 'id'>): Promise<string> {
    const id = uuidv4();
    const now = this.getCurrentTimestamp();
    const profile = await this.getCurrentProfile();
    const record: NotaAncla = {
      ...data,
      id,
      created_by: data.created_by || profile?.user_id || '',
      empresa_id: data.empresa_id || profile?.empresa_activa_id || '',
      created_at: data.created_at || now,
      updated_at: now,
      is_deleted: false,
    };
    await this.db.insert('nota_anclas', record);
    this.emitSync('INSERT', record);
    return id;
  }

  async update(data: Partial<NotaAncla> & { id: string }): Promise<void> {
    const updatedData = { ...data, updated_at: this.getCurrentTimestamp() };
    await this.db.update('nota_anclas', data.id, updatedData);
    this.emitSync('UPDATE', updatedData as any);
  }

  async delete(id: string): Promise<void> {
    await this.softDelete(id);
  }

  async getByNota(notaId: string): Promise<NotaAncla[]> {
    return this.db.queryMultiple<NotaAncla>('nota_anclas', [
      this.NOT_DELETED,
      { field: 'nota_id', operator: 'equals', value: notaId },
    ]);
  }

  async getByAncla(tipo: TipoAncla, anclaId: string): Promise<NotaAncla[]> {
    return this.db.queryMultiple<NotaAncla>('nota_anclas', [
      this.NOT_DELETED,
      { field: 'tipo_ancla', operator: 'equals', value: tipo },
      { field: 'ancla_id', operator: 'equals', value: anclaId },
    ]);
  }

  async getByCaravana(caravana: string): Promise<NotaAncla[]> {
    const vivas = ((await this.db.getAll('nota_anclas')) as NotaAncla[]).filter(a => !a.is_deleted);
    return vivas.filter(a =>
      (a.tipo_ancla === 'animal' && a.ancla_id === caravana)
      || (a.tipo_ancla === 'lote' && comoArray(a.caravanas).includes(caravana))
    );
  }
}
